"use client";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import { usePathname } from "next/navigation";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { UmbrellaPromoPageTestResult } from "@/monarch/rules/umbrellaPromoPage/types";
import DefaultAccordion from "../Default";
import Pathway from "../Pathway";
import TitleLeft from "../TitleLeft";
import TitleTop from "../TitleTop";
import { AccordionTextProps } from "../../types";
import AccordionTextItem from "../../AccordionTextItem";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton loader component that shows loading animation
 */
function AccordionSkeleton({
  variant,
}: {
  variant?: AccordionTextProps["variant"];
}) {
  return (
    <div className="w-full">
      {/* Title skeleton */}
      <Skeleton className="h-8 w-1/3 mb-6" />

      {/* Accordion items skeleton */}
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="border-b border-gray-200 py-4">
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Wrapper component that integrates AccordionText with Monarch for accordion control.
 * Only applies Monarch data when on /offers page, otherwise renders default variant.
 */
export default function MonarchAccordionWrapper(props: AccordionTextProps) {
  const pathname = usePathname();
  const isOffersPage = pathname === "/offers" || pathname?.endsWith("/offers");

  // If not on /offers page, just render the default variant
  if (!isOffersPage) {
    return <DefaultAccordion {...props} />;
  }

  const renderAccordionWithMonarch = (
    testResult: UmbrellaPromoPageTestResult | null,
  ) => {
    // Start with shortcode props as defaults
    let finalProps = { ...props };

    // Use Monarch accordion data if available
    if (testResult?.data?.faq) {
      const monarchAccordion = testResult.data.faq;

      // Override title if provided
      finalProps = {
        ...finalProps,
        ...(monarchAccordion.title && { title: monarchAccordion.title }),
      };

      // If Monarch provides items, create AccordionTextItem children
      if (monarchAccordion.items && monarchAccordion.items.length > 0) {
        finalProps.children = monarchAccordion.items.map((item, index) => (
          <AccordionTextItem key={index} trigger={item.title || ""}>
            {sanitizeAndParseInlineHTML(item.content ?? "")}
          </AccordionTextItem>
        ));
      }
      // Otherwise, keep the shortcode children
    }

    // Render the appropriate variant based on final props
    const { variant } = finalProps;
    switch (variant) {
      case "pathway":
        return <Pathway {...finalProps} />;
      case "title-left":
        return <TitleLeft {...finalProps} />;
      case "title-top":
        return <TitleTop {...finalProps} />;
      default:
        return <DefaultAccordion {...finalProps} />;
    }
  };

  const monarchAccordionComponent = (
    <MonarchDataConsumer<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      render={(testResult) => renderAccordionWithMonarch(testResult || null)}
    />
  );

  return (
    <MonarchResolver<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      Loader={<AccordionSkeleton variant={props.variant} />}
      variants={{
        control: monarchAccordionComponent,
        test: monarchAccordionComponent,
      }}
      fallbackVariant="control"
    />
  );
}
